import { supabase } from './supabaseClient';
import { getApiBaseUrl } from './config/apiConfig';
import logger from './utils/logger';

// ============ CONNECTION STATE MANAGEMENT ============

let isOnline = navigator.onLine;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_RECONNECT_DELAY = 1000; // Start with 1 second

// Connection state listeners
const connectionListeners = new Set();

// Notify all listeners of connection state changes
function notifyConnectionChange(online) {
  isOnline = online;
  connectionListeners.forEach(listener => listener(online));
}

// Setup global online/offline listeners
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    logger.info('🟢 Network connection restored');
    reconnectAttempts = 0; // Reset attempts on successful reconnection
    notifyConnectionChange(true);
  });

  window.addEventListener('offline', () => {
    logger.info('🔴 Network connection lost');
    notifyConnectionChange(false);
  });
}

// Subscribe to connection state changes
export function onConnectionChange(callback) {
  connectionListeners.add(callback);
  // Immediately notify of current state
  callback(isOnline);

  // Return unsubscribe function
  return () => connectionListeners.delete(callback);
}

// Get current connection state
export function getConnectionState() {
  return isOnline;
}

// ============ MESSAGE QUEUE FOR OFFLINE SUPPORT ============

const messageQueue = [];

// Add message to queue when offline
function queueMessage(messageData) {
  messageQueue.push({
    ...messageData,
    queuedAt: Date.now()
  });
  logger.debug(`📤 Message queued (${messageQueue.length} pending)`);
}

// Process queued messages when back online
export async function processMessageQueue() {
  if (!isOnline || messageQueue.length === 0) return;

  logger.info(`📨 Processing ${messageQueue.length} queued messages...`);

  const queue = [...messageQueue];
  messageQueue.length = 0; // Clear queue

  for (const msg of queue) {
    try {
      await sendMessage(msg);
      logger.debug('✅ Queued message sent successfully');
    } catch (error) {
      logger.error('❌ Failed to send queued message:', error);
      // Re-queue on failure
      messageQueue.push(msg);
    }
  }
}

// Auto-process queue when coming back online
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    setTimeout(processMessageQueue, 1000); // Small delay to ensure connection is stable
  });
}

// ============ EXISTING FUNCTIONS ============

// Fetch all conversations for a user (user1_id or user2_id)
export async function fetchConversations(userId) {
  const { data, error } = await supabase
    .from('conversations')
    .select('*')
    .or(`user1_id.eq.${userId},user2_id.eq.${userId}`)
    .order('created_at', { ascending: false });
  logger.debug('CHAT DEBUG: fetched conversations for', userId, '=', data);
  return { data, error };
}

// Fetch all messages for a conversation
export async function fetchMessages(conversationId) {
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });
  return { data, error };
}

// Send a new message (via backend with real-time scanning)
export async function sendMessage({ conversationId, senderId, receiverId, content, attachmentUrl, attachmentName }) {
  const messageData = {
    conversationId,
    senderId,
    receiverId,
    content,
    attachmentUrl,
    attachmentName
  };

  // If offline, queue the message and return pending status
  if (!isOnline) {
    logger.info('📴 Offline - queueing message for later delivery');
    queueMessage(messageData);
    return {
      data: {
        id: `queued-${Date.now()}`,
        conversation_id: conversationId,
        sender_id: senderId,
        content,
        attachment_url: attachmentUrl,
        attachment_name: attachmentName,
        created_at: new Date().toISOString(),
        queued: true
      },
      error: null,
      queued: true
    };
  }

  try {
    const response = await fetch(`${getApiBaseUrl()}/api/messages/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        conversationId,
        senderId,
        receiverId,
        content,
        attachmentUrl: attachmentUrl || null,
        attachmentName: attachmentName || null,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      // If request fails due to network, queue it
      if (!response.status || response.status >= 500) {
        logger.warn('🔄 Server error - queueing message for retry');
        queueMessage(messageData);
        return {
          data: {
            id: `queued-${Date.now()}`,
            conversation_id: conversationId,
            sender_id: senderId,
            content,
            attachment_url: attachmentUrl,
            attachment_name: attachmentName,
            created_at: new Date().toISOString(),
            queued: true
          },
          error: null,
          queued: true
        };
      }

      return {
        data: null,
        error: {
          message: result.error || 'Failed to send message',
          details: result.errors
        }
      };
    }

    // Log if message was auto-flagged
    if (result.auto_flagged) {
      logger.warn(`⚠️ Message auto-flagged: ${result.flags_count} issue(s) detected`);
    }

    return {
      data: result.data,
      error: null
    };
  } catch (error) {
    logger.error('Error sending message:', error);

    // Network error - queue the message
    if (error.message.includes('Failed to fetch') || error.message.includes('Network')) {
      logger.info('📡 Network error - queueing message');
      queueMessage(messageData);
      return {
        data: {
          id: `queued-${Date.now()}`,
          conversation_id: conversationId,
          sender_id: senderId,
          content,
          attachment_url: attachmentUrl,
          attachment_name: attachmentName,
          created_at: new Date().toISOString(),
          queued: true
        },
        error: null,
        queued: true
      };
    }

    return {
      data: null,
      error: {
        message: error.message || 'Network error while sending message'
      }
    };
  }
}

// ============ REAL-TIME SUBSCRIPTION WITH AUTO-RECONNECT ============

const activeSubscriptions = new Map(); // Track active subscriptions

// Subscribe to new messages in a conversation (real-time with auto-reconnect)
export function subscribeToMessages(conversationId, callback) {
  logger.debug('Setting up real-time subscription for conversation:', conversationId);

  let reconnectTimer = null;
  let currentChannel = null;
  let isSubscribed = false;

  // Calculate exponential backoff delay
  function getReconnectDelay() {
    const delay = Math.min(
      BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttempts),
      30000 // Max 30 seconds
    );
    return delay;
  }

  // Setup subscription
  function setupSubscription() {
    // Clean up previous channel if exists
    if (currentChannel) {
      supabase.removeChannel(currentChannel);
    }

    currentChannel = supabase.channel(`messages-${conversationId}`, {
      config: {
        broadcast: { self: true }
      }
    })
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        payload => {
          logger.debug('Real-time message received:', payload);
          callback(payload);
        }
      )
      .subscribe((status, error) => {
        logger.debug('Subscription status:', status, error);

        if (status === 'SUBSCRIBED') {
          isSubscribed = true;
          reconnectAttempts = 0; // Reset on success
          logger.info('✅ Real-time subscription active');
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          isSubscribed = false;
          logger.error('❌ Subscription error:', status, error);
          attemptReconnect();
        } else if (status === 'CLOSED') {
          isSubscribed = false;
          logger.warn('⚠️ Subscription closed');
          attemptReconnect();
        }
      });

    return currentChannel;
  }

  // Attempt to reconnect with exponential backoff
  function attemptReconnect() {
    // Don't reconnect if offline
    if (!isOnline) {
      logger.debug('⏸️ Skipping reconnect - offline');
      return;
    }

    // Don't reconnect if max attempts reached
    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      logger.error(`❌ Max reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) reached`);
      return;
    }

    reconnectAttempts++;
    const delay = getReconnectDelay();

    logger.info(`🔄 Reconnecting in ${delay}ms (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})...`);

    reconnectTimer = setTimeout(() => {
      logger.debug('🔄 Attempting to reconnect...');
      setupSubscription();
    }, delay);
  }

  // Setup initial subscription
  const channel = setupSubscription();

  // Listen for connection changes
  const unsubscribeConnection = onConnectionChange((online) => {
    if (online && !isSubscribed) {
      logger.info('🔄 Connection restored, resubscribing...');
      reconnectAttempts = 0;
      setupSubscription();
    }
  });

  // Store subscription info
  activeSubscriptions.set(conversationId, {
    channel,
    cleanup: () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      unsubscribeConnection();
      if (currentChannel) supabase.removeChannel(currentChannel);
    }
  });

  return channel;
}

// Unsubscribe from real-time updates
export function unsubscribeFromMessages(channel) {
  // Find and cleanup subscription by channel
  for (const [convId, sub] of activeSubscriptions.entries()) {
    if (sub.channel === channel) {
      sub.cleanup();
      activeSubscriptions.delete(convId);
      return;
    }
  }

  // Fallback: just remove channel
  if (channel) supabase.removeChannel(channel);
} 